import { Component, forwardRef, inject, DestroyRef, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { CurrencyModel } from '../../core/models/data/currency.model';
import { FinancialService } from '../../core/services/financial/financial.service';
import { ServiceResultModel } from '../../core/models/service/service-result.model';
import { LocalizationConstants } from '../../core/constants/settings.constants';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'ms-currency-dropdown',
  imports: [
    FormsModule, 
    ReactiveFormsModule,
    TitleCasePipe
  ],
  templateUrl: './currency-dropdown.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyDropdown),
      multi: true
    }
  ]
})
export class CurrencyDropdown implements OnInit, ControlValueAccessor {
  
  private destroyRef = inject(DestroyRef);
  private financialService = inject(FinancialService);

  public selectedCurrency: CurrencyModel | undefined = undefined;
  public selectedCurrencyId = signal<number>(LocalizationConstants.DEFAULT_CURRENCY_ID());
  public currenciesList = signal<CurrencyModel[] | undefined>(undefined);

  public isLoading: boolean = false;
  public disabled: boolean = false;

  public currency = new FormControl(0);

  private pendingValue: any = undefined;

  private onChange = (_value: any) => { };
  private onTouchedCallback = () => { };

  public ngOnInit(): void {
    this.currency.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        this.onChange(value);
      });

    this.loadCurrencies();
  }

  public async loadCurrencies(): Promise<void> {
    this.isLoading = true;

    try {
      const response: ServiceResultModel<CurrencyModel[]> = await this.financialService.getCurrencies();

      if (response?.data) {
        this.currenciesList.set(response.data);

        if (this.pendingValue !== undefined) {
          this.applyValue(this.pendingValue);
          this.pendingValue = undefined;
        } else if (!this.selectedCurrency && !this.selectedCurrencyId()) {
          this.setDefaultCurrency();
        }
      }
    } catch (error) {
      console.error('Error loading currencies:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private setDefaultCurrency(): void {
    const currencies = this.currenciesList();
    if (currencies && currencies.length > 0) {
      const defaultCurrencyId = LocalizationConstants.DEFAULT_CURRENCY_ID();
      const defaultCurrency = currencies.find(c => c.currencyId === defaultCurrencyId);
      if (defaultCurrency) {
        this.selectedCurrency = defaultCurrency;
        this.selectedCurrencyId.set(defaultCurrency.currencyId);
        this.currency.setValue(defaultCurrency.currencyId, { emitEvent: false });
        this.onChange(defaultCurrency);
      }
    }
  }

  public onSelectionChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    const currencyId = +target.value;
    const currency = this.currenciesList()?.find(c => c.currencyId === currencyId);

    this.selectedCurrency = currency;

    if (currency) {
      this.selectedCurrencyId.set(currency.currencyId);
    }

    this.onChange(currency);
  }

  public writeValue(value: any): void {
    const currencies = this.currenciesList();
    
    if (!currencies) {
      this.pendingValue = value;
      return;
    }

    this.applyValue(value);
  }

  private applyValue(value: any): void {
    if (typeof value === 'number') {
      this.selectedCurrencyId.set(value);
      this.currency.setValue(value, { emitEvent: false });
      const currencies = this.currenciesList();
      if (currencies) {
        this.selectedCurrency = currencies.find(c => c.currencyId === value);
      }
    } else if (value) {
      this.selectedCurrency = value;
      this.selectedCurrencyId.set(value?.currencyId || LocalizationConstants.DEFAULT_CURRENCY_ID());
      this.currency.setValue(value?.currencyId || LocalizationConstants.DEFAULT_CURRENCY_ID(), { emitEvent: false });
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onBlur(): void {
    this.onTouchedCallback();
  }
}
